import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import io from 'socket.io-client';
import Layout from '../components/Layout';
import Card from '../components/Card';
import Button from '../components/Button';
import MetaTags from '../components/MetaTags';
import { getSocketUrl } from '../utils/urlHelper';

const AudienceView = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [sessionInfo, setSessionInfo] = useState(null);
  const [question, setQuestion] = useState('');
  const [submitterName, setSubmitterName] = useState('');
  const [submittedQuestions, setSubmittedQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [socket, setSocket] = useState(null);
  const [currentPoll, setCurrentPoll] = useState(null);
  const [hasVoted, setHasVoted] = useState(false);
  const [reactionsEnabled, setReactionsEnabled] = useState(false);
  const [reactionIcons, setReactionIcons] = useState(['❤️', '👍', '👎']);
  const [clickedReaction, setClickedReaction] = useState(null); // Track which reaction was just clicked

  // Detect language based on browser locale with enhanced debugging
  const getLanguage = () => {
    try {
      // Get browser language with fallbacks
      const browserLang = navigator.language || 
                         (navigator.languages && navigator.languages[0]) || 
                         'en';
      
      console.log('Language detection debug:', {
        navigatorLanguage: navigator.language,
        navigatorLanguages: navigator.languages,
        browserLang,
        userAgent: navigator.userAgent
      });
      
      // Check for Swedish language codes
      const isSwedish = browserLang.toLowerCase().includes('sv') || 
                       browserLang.toLowerCase().includes('se') ||
                       browserLang.toLowerCase().startsWith('sv-');
      
      console.log('Swedish detection result:', isSwedish);
      
      return isSwedish ? 'sv' : 'en';
    } catch (error) {
      console.error('Error detecting language:', error);
      return 'en'; // Default to English on error
    }
  };

  const [language, setLanguage] = useState(getLanguage());
  
  // Add manual language override for debugging (can be removed in production)
  useEffect(() => {
    // Check for URL parameter to override language (for testing)
    const urlParams = new URLSearchParams(window.location.search);
    const langOverride = urlParams.get('lang');
    if (langOverride && (langOverride === 'sv' || langOverride === 'en')) {
      console.log('Language override from URL:', langOverride);
      setLanguage(langOverride);
    }
  }, []);

  // Enhanced language detection with server-side fallback
  useEffect(() => {
    const detectLanguageWithServer = async () => {
      try {
        // Try to get language from server based on IP geolocation
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        
        console.log('IP geolocation data:', data);
        
        // Check if user is in Sweden
        if (data.country_code === 'SE' || data.country === 'Sweden') {
          console.log('User detected in Sweden, switching to Swedish');
          setLanguage('sv');
        }
      } catch (error) {
        console.log('IP geolocation failed, using browser detection:', error);
        // Fallback to browser detection (already set in useState)
      }
    };

    // Only run server-side detection if browser detection didn't find Swedish
    if (language === 'en') {
      detectLanguageWithServer();
    }
  }, [language]);

  // Translations
  const translations = {
    en: {
      submitQuestion: 'Submit Your Question',
      submitDescription: 'Share your question with the presenter and audience',
      yourName: 'Your Name (optional)',
      yourNamePlaceholder: 'Enter your name (optional)',
      yourQuestion: 'Your Question',
      yourQuestionPlaceholder: 'Type your question here... Short answers are recommended. You have 260 characters left.',
      submitButton: 'Submit Question',
      submitting: 'Submitting...',
      submittedQuestions: 'Your Submitted Questions',
      questionsWillBeDisplayed: 'Questions will be displayed on screen when selected by the moderator',
      successMessage: 'Your question has been submitted successfully!',
      pollQuestion: 'Poll Question',
      voteForOption: 'Vote for an option',
      youVotedFor: 'You voted for',
      pollEnded: 'Poll has ended'
    },
    sv: {
      submitQuestion: 'Skicka Din Fråga',
      submitDescription: 'Dela din fråga med sändningen',
      yourName: 'Ditt Namn (valfritt)',
      yourNamePlaceholder: 'Ange ditt namn (valfritt)',
      yourQuestion: 'Din Fråga',
      yourQuestionPlaceholder: 'Skriv din fråga här... Korta meningar rekommenderas. Du har 260 tecken kvar.',
      submitButton: 'Skicka Fråga',
      submitting: 'Skickar...',
      submittedQuestions: 'Dina Skickade Frågor',
      questionsWillBeDisplayed: 'Frågor kommer att visas för moderator',
      successMessage: 'Din fråga har skickats framgångsrikt!',
      pollQuestion: 'Omröstningsfråga',
      voteForOption: 'Rösta på ett alternativ',
      youVotedFor: 'Du röstade på',
      pollEnded: 'Omröstningen har avslutats'
    }
  };

  const t = translations[language];

  // Initialize socket connection
  useEffect(() => {
    const socketUrl = getSocketUrl();
    const newSocket = io(socketUrl, {
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      timeout: 20000,
      forceNew: true,
      transports: ['websocket', 'polling']
    });
    setSocket(newSocket);

    // Clean up on unmount
    return () => {
      newSocket.disconnect();
    };
  }, []);

  // Handle page lifecycle changes for mobile browser reconnection
  useEffect(() => {
    const reconnectSocket = () => {
      console.log('Page became active, reconnecting socket...');
      const socketUrl = getSocketUrl();
      const newSocket = io(socketUrl, {
        reconnectionAttempts: 10,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000,
        timeout: 20000,
        forceNew: true,
        transports: ['websocket', 'polling']
      });
      setSocket(newSocket);
    };

    const rejoinRoom = () => {
      if (socket && socket.connected) {
        console.log('Page became active, re-joining session room...');
        socket.emit('join-room', sessionId);
        socket.emit('join-room', `session-${sessionId}`);
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden && socket) {
        if (socket.disconnected) {
          reconnectSocket();
        } else {
          rejoinRoom();
        }
      }
    };

    // Handle Page Lifecycle API states
    const handlePageLifecycleChange = (event) => {
      console.log('Page lifecycle state changed:', event.newState);
      
      switch (event.newState) {
        case 'active':
          // Page became active - reconnect if needed
          if (socket && socket.disconnected) {
            reconnectSocket();
          } else if (socket && socket.connected) {
            rejoinRoom();
          }
          break;
        case 'passive':
        case 'hidden':
          // Page is becoming inactive - no action needed
          console.log('Page becoming inactive:', event.newState);
          break;
        case 'frozen':
          // Page is frozen - socket will be disconnected
          console.log('Page frozen - socket will be disconnected');
          break;
        case 'terminated':
          // Page is terminated - socket will be disconnected
          console.log('Page terminated - socket will be disconnected');
          break;
      }
    };

    // Handle page resume from frozen state
    const handlePageResume = () => {
      console.log('Page resumed from frozen state');
      if (socket && socket.disconnected) {
        reconnectSocket();
      } else if (socket && socket.connected) {
        rejoinRoom();
      }
    };

    // Handle page show (back/forward cache)
    const handlePageShow = (event) => {
      console.log('Page show event, persisted:', event.persisted);
      if (event.persisted) {
        // Page was restored from back/forward cache
        if (socket && socket.disconnected) {
          reconnectSocket();
        } else if (socket && socket.connected) {
          rejoinRoom();
        }
      }
    };

    // Add all event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Page Lifecycle API events
    if ('onpagehide' in window) {
      document.addEventListener('pagehide', handlePageLifecycleChange);
    }
    if ('onpageshow' in window) {
      document.addEventListener('pageshow', handlePageShow);
    }
    if ('onfreeze' in document) {
      document.addEventListener('freeze', handlePageLifecycleChange);
    }
    if ('onresume' in document) {
      document.addEventListener('resume', handlePageResume);
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if ('onpagehide' in window) {
        document.removeEventListener('pagehide', handlePageLifecycleChange);
      }
      if ('onpageshow' in window) {
        document.removeEventListener('pageshow', handlePageShow);
      }
      if ('onfreeze' in document) {
        document.removeEventListener('freeze', handlePageLifecycleChange);
      }
      if ('onresume' in document) {
        document.removeEventListener('resume', handlePageResume);
      }
    };
  }, [socket, sessionId]);

  // Join session room when socket and sessionId are available
  useEffect(() => {
    if (socket && sessionId) {
      socket.emit('join-room', sessionId);
      socket.emit('join-room', `session-${sessionId}`);
      
      // Listen for question submission confirmation
      socket.on('new-question', (newQuestion) => {
        console.log('Received new question event:', newQuestion);
        if (newQuestion.session_id === parseInt(sessionId, 10)) {
          // Update the submitted questions list if it's from this session
          setSubmittedQuestions(prev => {
            // Check if we already have this question (avoid duplicates)
            if (!prev.some(q => q.id === newQuestion.id)) {
              return [...prev, newQuestion];
            }
            return prev;
          });
        }
      });

      // Listen for question deletion
      socket.on('question-deleted', (questionId) => {
        // Remove the deleted question from the submitted questions list
        setSubmittedQuestions(prev => prev.filter(q => q.id !== questionId));
      });

      // Listen for all questions cleared
      socket.on('questions-cleared', (sessionIdCleared) => {
        if (parseInt(sessionIdCleared, 10) === parseInt(sessionId, 10)) {
          // Clear all submitted questions for this session
          setSubmittedQuestions([]);
        }
      });

      // Listen for poll ask status updates (when "Ask" is toggled)
      socket.on('poll-ask-updated', (data) => {
        console.log('Received poll-ask-updated event:', data);
        if (String(data.sessionId) === String(sessionId)) {
          if (data.isAsked) {
            console.log('Poll is being asked, fetching poll details for voting');
            // Fetch the poll details
            api.get(`/api/polls/${data.pollId}`)
              .then(response => {
                const poll = response.data;
                console.log('Fetched poll for audience voting:', poll);
                setCurrentPoll(poll);
                setHasVoted(false); // Reset voting status for new poll
              })
              .catch(err => {
                console.error('Error fetching poll details for audience:', err);
              });
          } else {
            console.log('Poll is no longer being asked, hiding poll');
            setCurrentPoll(null);
            setHasVoted(false);
          }
        }
      });

      // Listen for reactions settings updates
      socket.on('reactions-settings-updated', (data) => {
        console.log('Received reactions-settings-updated event:', data);
        if (String(data.sessionId) === String(sessionId)) {
          setReactionsEnabled(data.reactionsEnabled);
          setReactionIcons(data.reactionIcons);
        }
      });

      // Listen for session name updates
      socket.on('session-name-updated', (data) => {
        console.log('Received session-name-updated event:', data);
        if (String(data.sessionId) === String(sessionId)) {
          setSessionInfo(prev => ({
            ...prev,
            name: data.name
          }));
        }
      });

      // Note: We don't show particles in AudienceView (performance on mobile)
      // Particles are only shown on DisplayView
    }
    
    return () => {
      if (socket) {
        socket.off('new-question');
        socket.off('question-deleted');
        socket.off('questions-cleared');
        socket.off('poll-ask-updated');
        socket.off('reactions-settings-updated');
        socket.off('audience-reaction');
        socket.off('session-name-updated');
      }
    };
  }, [socket, sessionId]);

  // Fetch session info and previously submitted questions
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch session info
        const sessionResponse = await api.get(`/api/sessions/${sessionId}`);
        const session = sessionResponse.data.session;
        setSessionInfo(session);
        
        // Load reactions settings
        if (session) {
          setReactionsEnabled(Boolean(session.reactions_enabled));
          if (session.reaction_icons) {
            try {
              const icons = JSON.parse(session.reaction_icons);
              setReactionIcons(icons);
            } catch (e) {
              console.error('Error parsing reaction icons:', e);
            }
          }
        }
        
        // Check if user has submitted questions before (using localStorage)
        const userQuestions = JSON.parse(localStorage.getItem(`questions_${sessionId}`)) || [];
        setSubmittedQuestions(userQuestions);
        
        // Load previously used name if available
        const savedName = localStorage.getItem(`submitter_name_${sessionId}`);
        if (savedName) {
          setSubmitterName(savedName);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching session info:', err);
        setError('Failed to load session information. The session may not exist or has ended.');
        setLoading(false);
      }
    };
    
    fetchData();
  }, [sessionId]);

  const handleQuestionChange = (e) => {
    setQuestion(e.target.value);
    // Clear any previous success/error messages when user starts typing
    if (success) setSuccess(null);
    if (error) setError(null);
  };

  const handleNameChange = (e) => {
    setSubmitterName(e.target.value);
    if (success) setSuccess(null);
    if (error) setError(null);
  };

  const handleSubmitQuestion = async (e) => {
    e.preventDefault();
    
    // Validate question
    if (!question.trim()) {
      setError('Please enter a question');
      return;
    }
    
    setSubmitting(true);
    
    // Save name to localStorage for future use
    if (submitterName.trim()) {
      localStorage.setItem(`submitter_name_${sessionId}`, submitterName.trim());
    }
    
    try {
      // Submit question to API
      const response = await api.post(`/api/sessions/${sessionId}/questions`, {
        content: question,
        submitterName: submitterName.trim() || undefined
      });
      
      // Add to local storage to track user's submitted questions
      const newQuestion = response.data;
      const userQuestions = JSON.parse(localStorage.getItem(`questions_${sessionId}`)) || [];
      userQuestions.push(newQuestion);
      localStorage.setItem(`questions_${sessionId}`, JSON.stringify(userQuestions));
      
      // Update state
      setSubmittedQuestions(userQuestions);
      setQuestion('');
      setSuccess(t.successMessage);
      
      // Track question submission
      if (window.umami) {
        window.umami.track('Question Submitted', { hasName: !!submitterName.trim() });
      }
      
      setSubmitting(false);
    } catch (err) {
      console.error('Error submitting question:', err);
      setError('Failed to submit your question. Please try again.');
      setSubmitting(false);
    }
  };

  const handlePollVote = async (optionIndex) => {
    if (!currentPoll || hasVoted) return;
    
    try {
      // Track poll vote
      if (window.umami) {
        window.umami.track('Poll Vote', { pollId: currentPoll.id });
      }
      
      // Generate a unique client ID for this session
      const clientId = `client_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const response = await api.post(`/api/polls/${currentPoll.id}/vote`, {
        option_index: optionIndex,
        client_id: clientId
      });
      
      console.log('Vote submitted:', response.data);
      setHasVoted(true);
      setSuccess(`You voted for option ${String.fromCharCode(65 + optionIndex)}`);
    } catch (error) {
      console.error('Error submitting vote:', error);
      setError('Failed to submit your vote. Please try again.');
    }
  };


  // Handle reaction click
  const handleReaction = (reaction, event) => {
    if (!socket || !reactionsEnabled) return;
    
    // Get click position
    const rect = event.target.getBoundingClientRect();
    const position = {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2
    };
    
    // Emit reaction to server (this will show particles on DisplayView)
    socket.emit('audience-reaction', {
      sessionId,
      reaction,
      position
    });
    
    // Show lightweight visual feedback (no particles for the user clicking)
    setClickedReaction(reaction);
    setTimeout(() => setClickedReaction(null), 300); // Clear after animation
    
    // Track reaction
    if (window.umami) {
      window.umami.track('Audience Reaction', { reaction });
    }
  };

  if (loading) {
    return (
      <Layout title="Loading Session..." showHeader={false}>
        <div className="text-center py-8">
          <p>Loading session information...</p>
        </div>
      </Layout>
    );
  }

  if (!sessionInfo) {
    return (
      <Layout title="Session Not Found" showHeader={false}>
        <Card>
          <div className="text-center py-4">
            <h2 className="text-xl font-bold text-red-600 mb-2">Session Not Found</h2>
            <p className="mb-4">{error || 'The session you are looking for does not exist or has ended.'}</p>
            <Button onClick={() => navigate('/')}>Return to Home</Button>
          </div>
        </Card>
      </Layout>
    );
  }

  return (
    <Layout 
      title={sessionInfo.name || 'Submit Your Question'} 
      showHeader={false}
      showFooter={false}
      backLink={null}
      fullWidth={true}
    >
      <MetaTags 
        title={`${sessionInfo.name || 'Live Session'} - AskTide`}
        description="Submit your questions and participate in live polls. Join the conversation and engage with the broadcast in real-time."
        url={window.location.href}
      />
      <div className="min-h-screen bg-gray-900">
        {/* AskTide Logo in top left */}
        <div className="fixed top-4 left-4 z-50 hidden">
          <a 
            href="https://www.asktide.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="block"
          >
            <img
              src="/asktide_logo.png"
              alt="AskTide"
              className="h-8 w-auto opacity-80 hover:opacity-100 transition-opacity"
            />
          </a>
        </div>
        
        {/* Language Debug Indicator (remove in production) */}
        {process.env.NODE_ENV === 'development' && (
          <div className="fixed top-4 right-4 z-50 bg-black bg-opacity-75 text-white px-3 py-1 rounded text-sm">
            Language: {language.toUpperCase()}
          </div>
        )}
        
        <div className="min-h-screen flex flex-col justify-center items-center px-4">
          {/* Session Title and Info - positioned above center */}
          <div className="text-center mb-8 max-w-4xl">
            
            <h1 className="text-4xl font-bold text-white mb-6 leading-tight">{sessionInfo.name}</h1>
          
          </div>

        {/* Main Content - centered in viewport */}
        <div className="w-full max-w-2xl">
          {/* Poll Modal Overlay */}
          {currentPoll && (
            <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
              <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  {/* Header with close button */}
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">{t.pollQuestion}</h2>
                      <p className="text-gray-600">{t.voteForOption}</p>
                    </div>
                    <button
                      onClick={() => setCurrentPoll(null)}
                      className="text-gray-400 hover:text-gray-600 focus:outline-none p-2 rounded-full hover:bg-gray-100"
                      aria-label="Close poll"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Poll content */}
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4">{currentPoll.question}</h3>
                    <div className="space-y-3">
                      {currentPoll.options.map((option, index) => (
                        <button
                          key={index}
                          onClick={() => handlePollVote(index)}
                          disabled={hasVoted}
                          className={`w-full p-4 rounded-lg border-2 transition-all ${
                            hasVoted
                              ? 'bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed'
                              : 'bg-white border-gray-300 text-gray-800 hover:border-blue-500 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                              hasVoted ? 'bg-gray-300 text-gray-600' : 'bg-blue-100 text-blue-600'
                            }`}>
                              {String.fromCharCode(65 + index)}
                            </span>
                            <span className="text-left">{option}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {hasVoted && (
                    <div className="p-4 bg-green-50 border border-green-200 text-green-700 rounded-md">
                      <p className="font-medium">{t.successMessage}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Question Submission Form */}
          <div className="w-full">
            {error && (
              <div className="mb-6 p-4 bg-red-900/20 border border-red-500/30 text-red-300 rounded-lg">
                {error}
              </div>
            )}
            
            {success && (
              <div className="mb-6 p-4 bg-green-900/20 border border-green-500/30 text-green-300 rounded-lg">
                {success}
              </div>
            )}
            
            <form onSubmit={handleSubmitQuestion} className="space-y-6">
              <div className="space-y-6">
                <div>
                  <input
                    id="submitterName"
                    type="text"
                    value={submitterName}
                    onChange={handleNameChange}
                    placeholder={t.yourNamePlaceholder}
                    className="w-full px-4 py-4 bg-gray-800 border border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-white placeholder-gray-400"
                  />
                </div>
                
                <div>
                  <textarea
                    id="question"
                    value={question}
                    onChange={handleQuestionChange}
                    rows={6}
                    maxLength={260}
                    placeholder={t.yourQuestionPlaceholder}
                    className="w-full px-4 py-4 bg-gray-800 border border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-white placeholder-gray-400 resize-none"
                    required
                  />
                  <div className="text-right text-sm text-gray-400 mt-1">
                    {question.length}/260
                  </div>
                </div>
                
                <div className="text-center">
                  <button 
                    type="submit" 
                    disabled={submitting || !question.trim()}
                    className={`px-12 py-4 rounded-lg font-semibold text-white transition-colors text-lg ${
                      submitting || !question.trim()
                        ? 'bg-gray-600 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900'
                    }`}
                  >
                    {submitting ? t.submitting : t.submitButton}
                  </button>
                </div>
                
                <div className="text-center text-gray-400 text-sm">
                  {t.questionsWillBeDisplayed}
                </div>
              </div>
            </form>
          </div>

        </div>
        </div>

        {/* Bottom Reactions Bar */}
        {reactionsEnabled && (
          <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-40">
            <div className="flex justify-center space-x-4">
              {reactionIcons.map((icon, index) => (
                <button
                  key={index}
                  onClick={(event) => handleReaction(icon, event)}
                  className={`w-16 h-16 rounded-full bg-white border-2 border-blue-500 hover:bg-blue-50 text-blue-600 text-2xl flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-110 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-blue-300 ${
                    clickedReaction === icon ? 'animate-reaction-pulse' : ''
                  }`}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>
        )}


      </div>

      <style>{`
        @keyframes reaction-pulse {
          0% {
            transform: scale(1);
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
          }
          50% {
            transform: scale(1.3);
            box-shadow: 0 20px 25px -5px rgba(59, 130, 246, 0.5), 0 10px 10px -5px rgba(59, 130, 246, 0.4);
            background-color: rgb(219, 234, 254);
          }
          100% {
            transform: scale(1);
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
          }
        }
        
        .animate-reaction-pulse {
          animation: reaction-pulse 0.3s ease-out;
        }
      `}</style>
    </Layout>
  );
};

export default AudienceView; 